import { useEffect, useState } from 'react';
import { filterNulls, useParameters } from '@web/utils/src';
import { useUserLineItemsQuery } from '@/api/orderLineItems.generated';
import {
  Article,
  Delivery,
  Inventory,
  Order,
  OrderLineItem,
  OrderStatus,
} from '@/types';

export type POrder = Pick<
  Order,
  | 'id'
  | 'status'
  | 'createdAt'
  | 'confirmedAt'
  | 'retailerID'
  | 'retailerOrderID'
>;

export type PLineItem = Pick<
  OrderLineItem,
  | 'id'
  | 'amount'
  | 'name'
  | 'unit'
  | 'quantity'
  | 'price'
  | 'lineTotalNet'
  | 'lineTotalQuantity'
  | 'retailerID'
> & {
  order: POrder;
  delivery?: null | Pick<Delivery, 'type' | 'siloName' | 'id'>;
  inventory?:
    | null
    | (Pick<Inventory, 'id' | 'visibleForCustomers'> & {
        articleData?: null | Pick<Article, 'id'>;
      });
};

const useUserLineItemsPagination = (perPage: number) => {
  const retailerId = useParameters<{ retailerId: string }>().retailerId;
  const [after, setAfter] = useState('1');
  const [cursors, setCursors] = useState<(string | undefined)[]>([]);
  const [result, setResult] = useState<Array<PLineItem>>([]);

  useEffect(() => {
    setResult([]);
    setCursors([]);
    setAfter('1');
  }, [retailerId]);

  const [{ data, error, fetching }, loadAgain] = useUserLineItemsQuery({
    variables: {
      filter: {
        retailerID: retailerId ? [retailerId] : null,
      },
      after,
      first: perPage,
    },
  });

  const hasNextPage = data?.currentUser?.lineItemsConnection?.edges?.some(
    (edge) => edge.nextCursor !== null,
  );
  const hasPreviousPage = cursors.length > 0;

  const endCursor = data?.currentUser?.lineItemsConnection?.pageInfo.endCursor;

  const loadMore = () => {
    if (!hasNextPage || fetching) return;
    if (error) console.log(error);

    const newCursors = [
      ...cursors,
      data?.currentUser?.lineItemsConnection.pageInfo.endCursor,
    ];
    setCursors(newCursors);
    setAfter(endCursor ?? '0');
  };

  const loadPrevious = () => {
    if (!hasPreviousPage || fetching) return;

    const newCursors = cursors.slice(0, -1);
    setCursors(newCursors);
    const prevCursor = newCursors[newCursors.length - 1] || '0';
    setAfter(prevCursor);
  };

  const refetch = () => {
    setAfter('1');
    setResult([]);
    setCursors([]);
    loadAgain();
  };

  const total = data?.currentUser?.lineItemsConnection?.pageInfo?.total || 0;

  useEffect(() => {
    if (data?.currentUser?.lineItemsConnection?.edges) {
      const newLineItemsResult = filterNulls(
        data.currentUser.lineItemsConnection?.edges.map(
          (edge) => edge.node || null,
        ),
      ).filter((node) => node.order?.status !== OrderStatus.Cancelled);

      // Reset results when going back to first page or refetching
      if (after === '1') {
        setResult(newLineItemsResult);
      } else {
        // Check if we're loading a new page (not already in results)
        const isNewPage =
          cursors.length > 0 && cursors[cursors.length - 1] !== after;

        setResult(newLineItemsResult);
        // if (isNewPage) {
        // } else {
        // 	setResult((prevState) => [...prevState, ...newLineItemsResult]);
        // }
      }
    }
  }, [data, after, cursors]);

  return {
    result,
    fetching,
    error,
    loadMore,
    loadPrevious,
    hasNextPage,
    hasPreviousPage,
    cursors,
    total,
    refetch,
  };
};

export default useUserLineItemsPagination;
